import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Box, Avatar, Chip, CircularProgress, Menu, MenuItem } from '@mui/material';
// Logo is now imported from public folder instead of using an icon
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
// Emergency Reset functionality is preserved in code but button is removed
// import RestartAltIcon from '@mui/icons-material/RestartAlt';
import UsageStatsWidget from './usage/UsageStatsWidget';
import { useAuth } from '../context/AuthContext';
import { apiGet } from '../utils/apiUtils';
import { useNavigate } from 'react-router-dom';

/**
 * AppHeader component - displays the application header with logo, title, and user authentication status
 */
const AppHeader = ({ onReset, onOpenProfile, onOpenSubscription, onOpenBilling, profileUpdateTrigger }) => {
  // Get authentication state from context
  const { user, loading, isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();

  // Dropdown menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Profile data state
  const [profileData, setProfileData] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  // Subscription status state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleMenuClose();
    onOpenProfile();
  };

  const handleSubscriptionClick = () => {
    handleMenuClose();
    onOpenSubscription();
  };

  const handleBillingClick = () => {
    handleMenuClose();
    onOpenBilling();
  };

  const handleAdminClick = () => {
    handleMenuClose();
    navigate('/admin');
  };

  // Function to load profile data
  const loadProfileData = async () => {
    if (!user?.id) {
      setProfileData(null);
      return;
    }

    setIsProfileLoading(true);
    try {
      const response = await apiGet('/api/profile');
      if (!response.ok) {
        console.error('Error loading profile data for header:', response.status, response.statusText);
        return;
      }

      const profileData = await response.json();
      setProfileData(profileData);
    } catch (error) {
      console.error('Error loading profile data for header:', error);
    } finally {
      setIsProfileLoading(false);
    }
  };

  // Function to load subscription status
  const loadSubscriptionStatus = async () => {
    if (!user?.id) {
      setSubscriptionStatus(null);
      setHasActiveSubscription(false);
      return;
    }

    try {
      const response = await apiGet('/api/stripe/subscription');
      if (!response.ok) {
        console.error('Error loading subscription status for header:', response.status, response.statusText);
        return;
      }

      const data = await response.json();
      const subscription = data.subscription;

      setSubscriptionStatus(subscription);

      // Check if user has an active subscription
      const isActive = subscription &&
        (subscription.status === 'active' || subscription.status === 'trialing');
      setHasActiveSubscription(isActive);
    } catch (error) {
      console.error('Error loading subscription status for header:', error);
    }
  };
  
  // Load profile data and subscription status when user is authenticated
  useEffect(() => {
    loadProfileData();
    loadSubscriptionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  
  // Reload profile data and subscription status when update is triggered
  useEffect(() => {
    if (profileUpdateTrigger > 0) {
      loadProfileData();
      loadSubscriptionStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileUpdateTrigger]);
  
  // User data display helpers
  
  // Format user display name
  const getUserDisplayName = () => {
    if (!user) return 'User';
    
    // If profile is loading and we have a display name, keep showing it
    if (isProfileLoading && profileData?.display_name) {
      return profileData.display_name;
    }
    
    // Priority: saved display_name → email username → fallbacks only if no profile data exists
    return (profileData?.display_name && profileData.display_name.trim()) ||
           user.email?.split('@')[0] || 
           user.user_metadata?.full_name || 
           user.user_metadata?.name || 
           'User';
  };
  
  // Get user avatar URL
  const getUserAvatar = () => {
    return user?.user_metadata?.avatar_url || 
           user?.user_metadata?.picture || 
           null;
  };
  
  return (
    <AppBar position="static" elevation={0} sx={{ 
      bgcolor: 'background.default', 
      borderBottom: 'none'
    }}>
      <Toolbar>
        {/* Removed wyspurAI branding from header since it's now in the sidebar */}
        <Box sx={{ flexGrow: 0 }} />
        
        <Box sx={{ flexGrow: 1 }} />
        
        {/* Usage Stats Widget - only show for authenticated users */}
        {isAuthenticated && (
          <Box sx={{ mr: 2 }}>
            <UsageStatsWidget compact={true} showDetails={true} />
          </Box>
        )}
        
        {/* Emergency Reset button removed for clean implementation */}
        
        {/* Authentication section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {loading ? (
            <CircularProgress size={24} color="primary" />
          ) : isAuthenticated ? (
            <>
              <Chip
                avatar={
                  getUserAvatar() 
                    ? <Avatar src={getUserAvatar()} alt={getUserDisplayName()} /> 
                    : <AccountCircleIcon />
                }
                label={getUserDisplayName()}
                variant="outlined"
                color="primary"
                onClick={handleMenuClick}
                deleteIcon={<ExpandMoreIcon />}
                onDelete={handleMenuClick}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              />
              
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={handleProfileClick}>
                  <PersonIcon sx={{ mr: 1 }} />
                  Profile
                </MenuItem>

                {/* Show "Admin Panel" only for internal tier users */}
                {profileData?.tier === 'internal' && (
                  <MenuItem onClick={handleAdminClick}>
                    <AdminPanelSettingsIcon sx={{ mr: 1 }} />
                    Admin Panel
                  </MenuItem>
                )}

                {/* Show "Manage Subscription" only for free/non-subscribed users */}
                {!hasActiveSubscription && (
                  <MenuItem onClick={handleSubscriptionClick}>
                    <CreditCardIcon sx={{ mr: 1 }} />
                    Manage Subscription
                  </MenuItem>
                )}

                {/* Show "Billing" only for subscribed users */}
                {hasActiveSubscription && (
                  <MenuItem onClick={handleBillingClick}>
                    <ReceiptIcon sx={{ mr: 1 }} />
                    Billing
                  </MenuItem>
                )}

                <MenuItem onClick={signOut}>
                  <LogoutIcon sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : null}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AppHeader;
